import type { ValidationIssue, SeverityLevel } from '../types';

export interface ComponentGroup {
  componentName: string;
  issues: ValidationIssue[];
  allElementIds: string[];
  elementCount: number;
  issueCount: number;
  worstSeverity: SeverityLevel;
  distinctMessages: string[];
}

export interface SystemGroup {
  systemName: string;
  components: ComponentGroup[];
  allElementIds: string[];
  elementCount: number;
  issueCount: number;
  worstSeverity: SeverityLevel;
}

const SEVERITY_RANK: Record<SeverityLevel, number> = {
  Critical: 0,
  Error: 1,
  Warning: 2,
  Info: 3,
};

/**
 * Extract component display name from an AffectedElements display string.
 * Strips trailing bracket/paren ID patterns like " [123456]" or " (ID: 123456)".
 */
export function extractComponentName(element: string): string {
  let name = element.trim();

  // Some connectivity payloads include trailing context, e.g.
  // "Pipes (ID: 201206) - Currently misconnected to 201787".
  // Keep only the left-most entity name.
  if (name.includes(' - ')) {
    name = name.split(' - ')[0].trim();
  }

  // Strip id patterns regardless of placement.
  name = name.replace(/\s*\[\d+\]\s*/g, ' ');
  name = name.replace(/\s*\(ID:\s*\d+\)\s*/gi, ' ');

  // Normalize extra whitespace.
  name = name.replace(/\s{2,}/g, ' ').trim();

  return name || element.trim();
}

export function resolveIssueSystemName(issue: ValidationIssue): string {
  const fromData = issue.AdditionalData?.systemName?.trim();
  return fromData || 'Unknown System';
}

export function resolveIssueComponentName(issue: ValidationIssue): string {
  const fromData = issue.AdditionalData?.componentName?.trim();
  if (fromData) {
    return fromData;
  }

  if (issue.AffectedElements.length > 0) {
    return extractComponentName(issue.AffectedElements[0]);
  }

  return 'Unknown Component';
}

function shouldSplitIssueByElement(issue: ValidationIssue): boolean {
  const elementCount = issue.ElementIds?.length ?? 0;
  if (elementCount <= 1) return false;

  // Keep pair issues intact (connectivity reconnect actions rely on 2 IDs).
  if (elementCount === 2) return false;

  return true;
}

function explodeIssueForGrouping(issue: ValidationIssue): Array<{ componentName: string; issue: ValidationIssue }> {
  if (!shouldSplitIssueByElement(issue)) {
    return [{ componentName: resolveIssueComponentName(issue), issue }];
  }

  const entries: Array<{ componentName: string; issue: ValidationIssue }> = [];
  const seen = new Set<string>();

  for (let idx = 0; idx < issue.ElementIds.length; idx += 1) {
    const elementId = issue.ElementIds[idx];
    if (!elementId) continue;

    const affectedLabel = issue.AffectedElements[idx] ?? '';
    const componentName = affectedLabel
      ? extractComponentName(affectedLabel)
      : `Element ${elementId}`;

    const dedupKey = `${componentName}::${elementId}`;
    if (seen.has(dedupKey)) continue;
    seen.add(dedupKey);

    const splitIssue: ValidationIssue = {
      ...issue,
      ElementIds: [elementId],
      AffectedElements: affectedLabel ? [affectedLabel] : issue.AffectedElements,
      AdditionalData: {
        ...(issue.AdditionalData ?? {}),
        componentName,
      },
    };

    entries.push({ componentName, issue: splitIssue });
  }

  if (entries.length === 0) {
    return [{ componentName: resolveIssueComponentName(issue), issue }];
  }

  return entries;
}

function worstOf(severities: SeverityLevel[]): SeverityLevel {
  let worst: SeverityLevel = 'Info';
  for (const s of severities) {
    if (SEVERITY_RANK[s] < SEVERITY_RANK[worst]) worst = s;
  }
  return worst;
}

/**
 * Group issues by system first, then by component.
 * Returns groups sorted by worst severity and issue count.
 */
export function groupIssuesBySystemAndComponent(issues: ValidationIssue[]): SystemGroup[] {
  const systemMap = new Map<string, Map<string, ValidationIssue[]>>();

  for (const issue of issues) {
    const systemName = resolveIssueSystemName(issue);
    const componentEntries = explodeIssueForGrouping(issue);

    for (const componentEntry of componentEntries) {
      const componentMap = systemMap.get(systemName) ?? new Map<string, ValidationIssue[]>();
      const groupedIssues = componentMap.get(componentEntry.componentName) ?? [];
      groupedIssues.push(componentEntry.issue);
      componentMap.set(componentEntry.componentName, groupedIssues);
      systemMap.set(systemName, componentMap);
    }
  }

  const systems: SystemGroup[] = [];
  for (const [systemName, componentMap] of Array.from(systemMap)) {
    const components: ComponentGroup[] = [];

    for (const [componentName, componentIssues] of Array.from(componentMap)) {
      const idSet = new Set<string>();
      const msgSet = new Set<string>();
      for (const issue of componentIssues) {
        for (const id of issue.ElementIds) {
          idSet.add(id);
        }
        msgSet.add(issue.Message);
      }

      components.push({
        componentName,
        issues: componentIssues,
        allElementIds: Array.from(idSet),
        elementCount: idSet.size,
        issueCount: componentIssues.length,
        worstSeverity: worstOf(componentIssues.map((i) => i.Severity)),
        distinctMessages: Array.from(msgSet),
      });
    }

    components.sort((a, b) => {
      const sevDiff = SEVERITY_RANK[a.worstSeverity] - SEVERITY_RANK[b.worstSeverity];
      if (sevDiff !== 0) return sevDiff;
      const issueDiff = b.issueCount - a.issueCount;
      if (issueDiff !== 0) return issueDiff;
      return a.componentName.localeCompare(b.componentName);
    });

    const allElementIds = Array.from(new Set(components.flatMap((c) => c.allElementIds)));
    const issueCount = components.reduce((sum, c) => sum + c.issueCount, 0);
    const worstSeverity = worstOf(components.map((c) => c.worstSeverity));

    systems.push({
      systemName,
      components,
      allElementIds,
      elementCount: allElementIds.length,
      issueCount,
      worstSeverity,
    });
  }

  systems.sort((a, b) => {
    const sevDiff = SEVERITY_RANK[a.worstSeverity] - SEVERITY_RANK[b.worstSeverity];
    if (sevDiff !== 0) return sevDiff;
    const issueDiff = b.issueCount - a.issueCount;
    if (issueDiff !== 0) return issueDiff;
    return a.systemName.localeCompare(b.systemName);
  });

  return systems;
}
